import { useCallback, useEffect, useState } from 'react';
import type { ArrayFromExperiments, Experiments } from '@cbhq/experiments';

import { ApiEnvironmentMapping, targetEnvironment } from '../../../configs/environments';
import { workspaceExperimentConfig } from '../appExperiments';

const UUID_SUBJECT_TYPE = 6;
const WEB_CLIENT_TYPE = 3;

type CiferGroup = { test: string; group: string; isTracked: boolean };
type CiferResponse = { groups: CiferGroup[] };

// CIFER only supports dev and prod environments.
// Local uses empty host (reverse proxy). Staging routes through workspace-staging.
function getCiferHost(): string {
  if (targetEnvironment === 'local') return '';

  if (targetEnvironment === 'production') {
    return ApiEnvironmentMapping.production.host;
  }

  if (targetEnvironment === 'staging') {
    return ApiEnvironmentMapping.staging.host;
  }

  return ApiEnvironmentMapping.development.host;
}

const baseUrl = `${getCiferHost()}/api/v3/coinbase.experiment.ExperimentService/ListSplitTestGroups`;

function buildUrl(subjectId: string): string {
  const query = {
    subject_id: subjectId,
    client: { type: WEB_CLIENT_TYPE },
    subject_type: UUID_SUBJECT_TYPE,
  };
  return `${baseUrl}?${new URLSearchParams({ q: btoa(JSON.stringify(query)) })}`;
}

export function useGetCiferExperiments(subjectId: string): {
  loading: boolean;
  experiments: ArrayFromExperiments<Experiments>;
} {
  const [experiments, setExperiments] = useState<ArrayFromExperiments<Experiments>>([]);
  const [loading, setLoading] = useState(true);

  const fetchExperiments = useCallback(async () => {
    if (!subjectId || targetEnvironment === 'local') {
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(buildUrl(subjectId), {
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          'x-enabled-features': 'targeting',
        },
      });

      if (!res.ok) return;

      const json = (await res.json()) as CiferResponse;
      const known = Object.keys(workspaceExperimentConfig);

      setExperiments(
        (json.groups ?? [])
          .filter((g) => known.includes(g.test))
          .map((g) => ({ name: g.test, group: g.group, skipTracking: !g.isTracked })),
      );
    } catch {
      // Silently fall back to empty experiments on fetch failure (e.g. local dev CORS)
    } finally {
      setLoading(false);
    }
  }, [subjectId]);

  useEffect(() => {
    fetchExperiments();
  }, [fetchExperiments]);

  return { loading, experiments };
}
